import { useCallback, useEffect, useMemo, useState } from "react";

import { useTodaysScannedDevices } from "../cacheQueries";
import { cardsForUtcDay, isCardDismissed, mergeBellCards } from "../lib/bellCards";
import { useUtcDay } from "./useUtcDay";
import {
  dismissBellSession,
  dismissBellSessions,
  getBellNotifications,
  getDismissedBellSessions,
  type BellNotification,
} from "../utils/notificationUtils";

/**
 * Today's flights, for the bell.
 *
 * Merges the backend's detection rows (one per flight session — the same rows
 * the intrusion log shows) with the WebSocket entry for a flight still in
 * progress, which has no row yet. Without the rows the bell only knew what this
 * tab had personally witnessed, so a reload emptied it and anything that flew
 * before the tab opened was invisible.
 */
export function useBellCards(): {
  cards: BellNotification[];
  /**
   * True when the backing query failed and there is nothing to show from it.
   * Without this the bell rendered a failed fetch as an authoritative
   * "No notifications" — indistinguishable from a quiet day.
   */
  loadFailed: boolean;
  retry: () => void;
  dismiss: (id: string) => void;
  clearAll: () => void;
} {
  const { data: rows, isError, refetch } = useTodaysScannedDevices();

  // The live half still lives in localStorage, written by the WS path, and
  // announces itself through the bell-update event.
  const [liveCards, setLiveCards] = useState<BellNotification[]>([]);
  const [dismissed, setDismissed] = useState<string[]>([]);

  useEffect(() => {
    const sync = () => {
      setLiveCards(getBellNotifications());
      setDismissed(getDismissedBellSessions());
    };
    sync();
    window.addEventListener("bellNotificationsUpdate", sync);
    return () => window.removeEventListener("bellNotificationsUpdate", sync);
  }, []);

  // Flips at midnight UTC (the backend's "today" bucket), so yesterday's
  // flights leave the bell even in a tab that never refetches.
  const utcDay = useUtcDay();

  const cards = useMemo(() => {
    // Flight-shaped dismissal, not exact-id: the id the user dismissed under
    // (live card) and the id on screen next render (backend row) describe the
    // same flight with different strings — see isCardDismissed.
    return cardsForUtcDay(
      mergeBellCards(rows, liveCards, (card) => isCardDismissed(card, dismissed)),
      utcDay,
    );
  }, [rows, liveCards, dismissed, utcDay]);

  const dismiss = useCallback((id: string) => {
    dismissBellSession(id);
    setDismissed(getDismissedBellSessions());
  }, []);

  // "Clear" dismisses exactly what is on screen. It cannot simply wipe storage:
  // the cards are rebuilt from a query, so anything not explicitly dismissed
  // would reappear on the next render.
  const clearAll = useCallback(() => {
    dismissBellSessions(cards.map((c) => c.id));
    setDismissed(getDismissedBellSessions());
  }, [cards]);

  // Failed AND nothing held over (keepPreviousData usually bridges a failed
  // refetch) — only then is the empty state a lie worth flagging.
  const loadFailed = isError && rows === undefined;
  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  return { cards, loadFailed, retry, dismiss, clearAll };
}
